import { useState } from 'react';
import PropTypes from 'prop-types';
import { Draggable } from '@hello-pangea/dnd';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import AvatarGroup, { avatarGroupClasses } from '@mui/material/AvatarGroup';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';

// hooks
import { useBoolean } from 'src/hooks/use-boolean';
import { bgBlur } from 'src/theme/css';

// components
import Iconify from 'src/components/iconify';
import CustomPopover, { usePopover } from 'src/components/custom-popover';
import { useSnackbar } from 'src/components/snackbar';

// Task detail components
import AdminCardHeader from './kanban-card-details/header-input';
import TaskLabels from './kanban-card-details/task-labels';
import TaskDescription from './kanban-card-details/description';
import TaskAttachments from './kanban-card-details/attachment';
import TaskActivity from './kanban-card-details/activity';
import Members from './kanban-card-details/members';

// Sample options (replace with your actual data)
const labelOptions = [
  { id: 1, name: 'BUG', color: '#f44336' },
  { id: 2, name: 'High Priority', color: '#ff9800' },
  { id: 3, name: 'Feature', color: '#4caf50' },
  { id: 4, name: 'Enhancement', color: '#2196f3' }
];

const memberOptions = [
  { id: 'BA', name: 'Bisma Amanat', avatarUrl: '/path/to/avatar1.jpg' },
  { id: 'HB', name: 'Hassan B', avatarUrl: '/path/to/avatar2.jpg' }
];

export default function KanbanTaskItem({ task, index, onDeleteTask, reload, onUpdateTask, sx, ...other }) {
  const theme = useTheme();
  const openDetails = useBoolean();
  const popover = usePopover();
  const { enqueueSnackbar } = useSnackbar();
  const [ hover, setHover ] = useState(false);
  const confirm = useBoolean();

  // Task state
  const [ taskName, setTaskName ] = useState(task?.title || task?.name || '');
  const [ taskDescription, setTaskDescription ] = useState(task?.description || '');
  const [ attachments, setAttachments ] = useState(task?.attachments || []);
  const [ comments, setComments ] = useState(task?.comments || []);
  const [ selectedLabels, setSelectedLabels ] = useState(
    task?.labels ? (typeof task.labels === 'string' ? JSON.parse(task.labels) : task.labels) : [ 'BUG', 'High Priority' ]
  );
  const [ selectedMembers, setSelectedMembers ] = useState(
    task?.assigned_users?.map(user => `${user.first_name.charAt(0)}${user.last_name.charAt(0)}`) ||
    task?.assignee?.map(user => user.id) ||
    [ 'BA', 'HB' ]
  );
  const [ selectedPriority, setSelectedPriority ] = useState(task?.priority || 'high');

  // Extract view and attachment counts from task
  const viewCount = task?.views || 0;
  const attachmentCount = attachments?.length || task?.attachmentCount || 0;
  const commentCount = comments?.length || task?.commentCount || 1;

  // Check if task has an image attachment
  const hasImage = false;


  // Handle update
  const handleUpdate = async (updatedData) => {
    try {
      const formData = new FormData();

      formData.append('title', updatedData.name || taskName);
      formData.append('description', updatedData.description || taskDescription);
      formData.append('status', updatedData.status || task.status);
      formData.append('priority', updatedData.priority || selectedPriority);
      formData.append('labels', JSON.stringify(updatedData.labels || selectedLabels));
      formData.append('assignee', JSON.stringify(updatedData.assignee || selectedMembers));

      // Call the update function from props
      if (onUpdateTask) {
        await onUpdateTask(formData, task.id);
        enqueueSnackbar('Task updated successfully!', {
          variant: 'success',
          anchorOrigin: { vertical: 'top', horizontal: 'center' },
        });
      }
    } catch (error) {
      console.error('Error updating task:', error);
      enqueueSnackbar('Failed to update task', {
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
      });
    }
  };

  // Handle task deletion
  const handleDelete = async () => {
    try {
      if (onDeleteTask) {
        await onDeleteTask();
      }
      reload()
      confirm.onFalse();
      openDetails.onFalse();
    } catch (error) {
      console.error('Error deleting task:', error);
      enqueueSnackbar('Failed to delete task', {
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
      });
    }
  };

  // Confirm Dialog
  const renderConfirmDialog = (
    <Dialog open={ confirm.value } onClose={ confirm.onFalse }>
      <DialogTitle>Delete Task</DialogTitle>
      <DialogContent>
        <Typography>Are you sure you want to delete this task?</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={ confirm.onFalse } color="inherit">Cancel</Button>
        <Button onClick={ handleDelete } color="error" variant="contained">Delete</Button>
      </DialogActions>
    </Dialog>
  );

  // Task info renderer with view count and file attachments
  const renderInfo = (
    <Stack direction="row" alignItems="center" spacing={ 1 }>
      <Stack
        flexGrow={ 1 }
        direction="row"
        alignItems="center"
        sx={ {
          typography: 'caption',
          color: 'text.disabled',
        } }
      >
        <Iconify width={ 16 } icon="solar:eye-bold" sx={ { mr: 0.25 } } />
        <Box component="span" sx={ { mr: 1 } }>
          { viewCount }
        </Box>

        <Iconify width={ 16 } icon="solar:chat-round-dots-bold" sx={ { mr: 0.25 } } />
        <Box component="span" sx={ { mr: 1 } }>
          { commentCount }
        </Box>

        <Iconify width={ 16 } icon="eva:attach-2-fill" sx={ { mr: 0.25 } } />
        <Box component="span">{ attachmentCount }</Box>
      </Stack>

      <AvatarGroup
        max={ 3 }
        sx={ {
          [ `& .${avatarGroupClasses.avatar}` ]: {
            width: 24,
            height: 24,
            fontSize: 12,
            bgcolor: theme => {
              return theme.palette.mode === 'dark' ? 'primary.main' : 'primary.light';
            }
          },
        } }
      >
        { selectedMembers.map((memberId, index) => {
          const user = memberOptions.find(m => m.id === memberId);
          return (
            <Avatar
              key={ index }
              alt={ user?.first_name || memberId }
              src={ user?.avatarUrl || '' }
              sx={ {
                bgcolor: index % 2 === 0 ? 'primary.main' : 'error.main',
                color: '#fff'
              } }
            >
              { memberId }
            </Avatar>
          );
        }) }
      </AvatarGroup>
    </Stack>
  );

  // Labels renderer (simplified for the card view)
  const renderLabels = selectedLabels && selectedLabels.length > 0 && (
    <Box sx={ { mb: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 } }>
      { selectedLabels.slice(0, 2).map((label, index) => {
        const labelData = labelOptions.find(l => l.name === label) || { color: '#f44336' };
        return (
          <Box
            key={ index }
            sx={ {
              height: 6,
              width: 36,
              borderRadius: '3px',
              bgcolor: labelData.color || '#f44336',
              mx: 0.25
            } }
          />
        );
      }) }
    </Box>
  );

  // Detail dialog content
  const renderTaskDetails = (
    <Dialog open={ openDetails.value } onClose={ openDetails.onFalse } fullWidth maxWidth="md">
      <DialogContent sx={ { p: 3 } }>
        <AdminCardHeader
          task={ task }
          taskName={ taskName }
          onChangeTaskName={ (newName) => {
            setTaskName(newName);
            handleUpdate({ ...task, name: newName });
          } }
          onClose={ openDetails.onFalse }
        />

        <TaskLabels
          selectedLabels={ selectedLabels }
          onChange={ (labels) => {
            setSelectedLabels(labels);
            handleUpdate({ ...task, labels });
          } }
          labelOptions={ labelOptions }
        />

        <Members
          selectedMembers={ selectedMembers }
          onChange={ (members) => {
            setSelectedMembers(members);
            handleUpdate({
              ...task, assignee: members.map(id =>
                memberOptions.find(m => m.id === id)
              )
            });
          } }
          memberOptions={ memberOptions }
        />

        <TaskDescription
          description={ taskDescription }
          onChange={ (updatedText) => {
            setTaskDescription(updatedText);
            handleUpdate({ ...task, description: updatedText });
          } }
        />

        <TaskAttachments
          attachments={ attachments }
          onChange={ (updatedAttachments) => {
            setAttachments(updatedAttachments);
            handleUpdate({ ...task, attachments: updatedAttachments });
          } }
        />

        <TaskActivity
          comments={ comments }
          memberOptions={ memberOptions }
          onAddComment={ (newComment) => {
            const updatedComments = [ ...comments, newComment ];
            setComments(updatedComments);
            handleUpdate({ ...task, comments: updatedComments });
          } }
        />
      </DialogContent>
    </Dialog>
  );

  // Options popover
  const renderPopover = (
    <CustomPopover
      open={ popover.open }
      onClose={ popover.onClose }
      arrow="right-top"
      sx={ { width: 140 } }
    >
      <Stack spacing={ 1 } sx={ { p: 1 } }>
        <Box
          sx={ {
            display: 'flex',
            alignItems: 'center',
            p: 1,
            '&:hover': { bgcolor: 'action.hover', borderRadius: 1 },
            cursor: 'pointer',
          } }
          onClick={ () => {
            popover.onClose();
            confirm.onTrue();
          } }
        >
          <Iconify icon="eva:trash-2-outline" sx={ { mr: 1, color: 'error.main' } } />
          <Typography variant="body2">Delete</Typography>
        </Box>

        <Box
          sx={ {
            display: 'flex',
            alignItems: 'center',
            p: 1,
            '&:hover': { bgcolor: 'action.hover', borderRadius: 1 },
            cursor: 'pointer',
          } }
          onClick={ () => {
            popover.onClose();
            openDetails.onTrue();
          } }
        >
          <Iconify icon="eva:edit-outline" sx={ { mr: 1 } } />
          <Typography variant="body2">Edit</Typography>
        </Box>
      </Stack>
    </CustomPopover>
  );

  return (
    <>
      <Draggable draggableId={ task.id.toString() } index={ index }>
        { (provided, snapshot) => (
          <Paper
            ref={ provided.innerRef }
            { ...provided.draggableProps }
            { ...provided.dragHandleProps }
            onMouseEnter={ () => setHover(true) }
            onMouseLeave={ () => setHover(false) }
            sx={ {
              width: 1,
              borderRadius: 1.5,
              overflow: 'hidden',
              position: 'relative',
              bgcolor: theme => theme.palette.mode === 'dark' ? '#2a2e3b' : 'background.default',
              boxShadow: theme.customShadows.z1,
              '&:hover': {
                boxShadow: theme.customShadows.z20,
              },
              ...(openDetails.value && {
                boxShadow: theme.customShadows.z20,
              }),
              ...(snapshot.isDragging && {
                boxShadow: theme.customShadows.z20,
                ...bgBlur({
                  opacity: 0.48,
                  color: theme.palette.background.default,
                }),
              }),
              ...sx,
            } }
            { ...other }
          >
            {/* Delete icon that appears on hover */ }
            { hover && (
              <IconButton
                size="small"
                color="error"
                sx={ {
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  zIndex: 9,
                  opacity: 0.8,
                  '&:hover': { opacity: 1 },
                } }
                onClick={ (e) => {
                  e.stopPropagation();
                  confirm.onTrue();
                } }
              >
                <Iconify icon="eva:trash-2-outline" width={ 20 } />
              </IconButton>
            ) }

            {/* Labels at top */ }
            <Box
              sx={ { p: 0.5, pt: 1 } }
              onClick={ openDetails.onTrue }
            >
              { renderLabels }
            </Box>

            <Stack
              spacing={ 1 }
              sx={ { px: 2, pb: 2, position: 'relative' } }
              onClick={ openDetails.onTrue }
            >
              <Typography
                variant="subtitle2"
                sx={ {
                  color: theme => theme.palette.mode === 'dark' ? '#fff' : 'text.primary',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  mb: 0.5
                } }
              >
                { taskName }
              </Typography>

              <Typography
                variant="body2"
                sx={ {
                  color: theme => theme.palette.mode === 'dark' ? 'text.secondary' : 'text.secondary',
                  fontSize: '0.75rem',
                  mb: 1.5,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden'
                } }
              >
                { taskDescription }
              </Typography>

              { renderInfo }
            </Stack>
          </Paper>
        ) }
      </Draggable>

      { renderTaskDetails }
      { renderPopover }
      { renderConfirmDialog }
    </>
  );
}

KanbanTaskItem.propTypes = {
  index: PropTypes.number,
  onDeleteTask: PropTypes.func,
  onUpdateTask: PropTypes.func,
  sx: PropTypes.object,
  task: PropTypes.object,
};